import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminOrder } from '../../mocks/adminOrders';

interface CancelledOrderToastProps {
  orders: AdminOrder[];
  onDismiss: () => void;
}

const AUTO_DISMISS_MS = 10000;

export default function CancelledOrderToast({ orders, onDismiss }: CancelledOrderToastProps) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(100);
  const [leaving, setLeaving] = useState(false);
  const first = orders[0];

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(onDismiss, 280);
  }, [onDismiss]);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct <= 0) {
        clearInterval(tick);
        dismiss();
      }
    }, 80);
    return () => clearInterval(tick);
  }, [dismiss]);

  const handleView = () => {
    dismiss();
    setTimeout(() => navigate('/admin/orders'), 100);
  };

  return (
    <div
      className={`w-80 bg-white rounded-2xl border border-red-200 overflow-hidden ${
        leaving ? 'animate-slide-out-right' : 'animate-slide-in-right'
      }`}
      style={{ boxShadow: '0 8px 32px rgba(220,38,38,0.12)' }}
    >
      {/* Timer bar */}
      <div className="h-1 bg-red-100">
        <div
          className="h-full bg-red-500 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-close-circle-fill text-red-500 text-base" />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900 leading-tight">
                {orders.length === 1 ? 'Order cancelled by customer' : `${orders.length} orders cancelled`}
              </p>
              <p className="text-xs text-stone-400 mt-0.5">
                {orders.length === 1 ? `Order #${first.id}` : 'Multiple orders were cancelled'}
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer transition text-stone-400 flex-shrink-0"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Order detail card */}
        <div className="bg-red-50 rounded-xl px-3 py-2.5 mb-3 border border-red-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-stone-700">{first.id}</span>
            <span className="text-xs font-bold text-red-600">GYD {first.total.toLocaleString()}</span>
          </div>
          <p className="text-xs font-medium text-stone-600 truncate">{first.customer}</p>
          <p className="text-xs text-stone-400 truncate mt-0.5">
            <i className="ri-map-pin-line mr-0.5" />
            {first.address}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <i className="ri-money-dollar-circle-line text-[11px] text-stone-400" />
            <span className="text-[11px] text-stone-500">{first.paymentMethod}</span>
          </div>
          {orders.length > 1 && (
            <p className="text-xs text-stone-400 mt-2 font-medium border-t border-red-100 pt-1.5">
              +{orders.length - 1} more cancellation{orders.length > 2 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={dismiss}
            className="flex-1 py-2 text-xs font-semibold text-stone-500 hover:text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 cursor-pointer transition whitespace-nowrap"
          >
            Dismiss
          </button>
          <button
            onClick={handleView}
            className="flex-1 py-2 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer transition whitespace-nowrap flex items-center justify-center gap-1"
          >
            <i className="ri-eye-line text-xs" />
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
}
